import type { OztMethod, OztMethodPayloadMap, OztMethodType } from '../../consts/ozonetel/methods';
import { isOztMethod, OZT_METHOD_TYPES } from '../../consts/ozonetel/methods';
import type {
  OzonetelInitializeEventPayload,
  OzonetelStartCallPayload,
} from '../../consts/ozonetel/sdk';
import { OZONE_TEL_AGENT_MODES, OZONE_TEL_AGENT_STATUSES } from '../../consts/ozonetel/sdk';
import type { OztPhoneNumberPayload, OztSetModePayload } from '../../consts/ozonetel/shared';

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function parseInitializePayload(payload: unknown): OzonetelInitializeEventPayload | undefined {
  if (!isObjectRecord(payload)) return undefined;
  const { agentid, phoneNumber, pin } = payload;
  if (typeof agentid !== 'string' || typeof phoneNumber !== 'string') return undefined;
  if (typeof pin !== 'number' || !Number.isFinite(pin)) return undefined;
  return { agentid, phoneNumber, pin };
}

function parseStartCallPayload(payload: unknown): OzonetelStartCallPayload | undefined {
  if (!isObjectRecord(payload)) return undefined;
  const { campaignId, phoneNumber, redial } = payload;
  if (typeof campaignId !== 'string') return undefined;
  if (typeof phoneNumber !== 'number' || !Number.isFinite(phoneNumber)) return undefined;
  if (redial !== undefined && typeof redial !== 'boolean') return undefined;
  return redial === undefined ? { campaignId, phoneNumber } : { campaignId, phoneNumber, redial };
}

function parseSetModePayload(payload: unknown): OztSetModePayload | undefined {
  if (!isObjectRecord(payload)) return undefined;
  const { mode, status, reconnect } = payload;
  const modeOk =
    typeof mode === 'string' && OZONE_TEL_AGENT_MODES.includes(mode as OztSetModePayload['mode']);
  const statusOk =
    typeof status === 'string' &&
    OZONE_TEL_AGENT_STATUSES.includes(status as OztSetModePayload['status']);
  if (!modeOk || !statusOk || typeof reconnect !== 'boolean') return undefined;
  return {
    mode: mode as OztSetModePayload['mode'],
    status: status as OztSetModePayload['status'],
    reconnect,
  };
}

function parsePhoneNumberPayload(payload: unknown): OztPhoneNumberPayload | undefined {
  if (payload === undefined) return undefined;
  if (!isObjectRecord(payload)) return undefined;
  const { phoneNumberToHold } = payload;
  if (phoneNumberToHold !== undefined && typeof phoneNumberToHold !== 'number') return undefined;
  return phoneNumberToHold === undefined ? {} : { phoneNumberToHold };
}

function toNoPayloadMethod<T extends OztMethodType>(
  eventType: T,
): { eventType: T; eventPayload: OztMethodPayloadMap[T] } {
  return { eventType, eventPayload: undefined as OztMethodPayloadMap[T] };
}

export const toOztMethod = (eventType?: string, eventPayload?: unknown): OztMethod | undefined => {
  if (!isOztMethod(eventType)) return undefined;

  switch (eventType) {
    case OZT_METHOD_TYPES.initialize: {
      const parsed = parseInitializePayload(eventPayload);
      return parsed ? { eventType, eventPayload: parsed } : undefined;
    }
    case OZT_METHOD_TYPES.startCall: {
      const parsed = parseStartCallPayload(eventPayload);
      return parsed ? { eventType, eventPayload: parsed } : undefined;
    }
    case OZT_METHOD_TYPES.hold:
    case OZT_METHOD_TYPES.unhold: {
      const parsed = parsePhoneNumberPayload(eventPayload);
      return eventPayload === undefined || parsed ? { eventType, eventPayload: parsed } : undefined;
    }
    case OZT_METHOD_TYPES.setMode: {
      const parsed = parseSetModePayload(eventPayload);
      return parsed ? { eventType, eventPayload: parsed } : undefined;
    }
    case OZT_METHOD_TYPES.endCall:
    case OZT_METHOD_TYPES.mute:
    case OZT_METHOD_TYPES.unmute:
    case OZT_METHOD_TYPES.muteAgent:
    case OZT_METHOD_TYPES.unmuteAgent:
    case OZT_METHOD_TYPES.logout:
    case OZT_METHOD_TYPES.getCallData:
    case OZT_METHOD_TYPES.releaseAgent:
      return toNoPayloadMethod(eventType);
    default:
      return undefined;
  }
};
